import { NextApiResponse } from 'next';
import { Webhook } from 'standardwebhooks';
import { Config } from 'core/common/contexts';
import { RevalidationError, RevalidationErrorReason } from 'core/common/errors/RevalidationError';
import { normalizeError } from 'core/common/errors/normalizeError';
import { Logger } from 'core/common/services/logger/interfaces';
import { removeLeadingSlash } from 'core/common/utils/string';
import { uniq } from 'core/common/utils/uniq';
import { RequestFunnelWithAllFlows, RequestFunnelWithOneFlow } from 'core/funnel/entities';
import { Languages } from 'core/localization/entities';

type RevalidateFunnelParams = {
  res: NextApiResponse;
  locale: Languages;
  funnelName: string;
  screenNames: Array<string>;
};

type RevalidatePathsParams = {
  res: NextApiResponse;
  paths: Array<string>;
};

type VerifySignaturesParams = {
  secret: string;
  payload: string | Buffer;
  headers: {
    'webhook-id': string;
    'webhook-timestamp': string;
    'webhook-signature': string;
  };
};

export type RevalidationStrategyParams = {
  res: NextApiResponse;
  data: RequestFunnelWithAllFlows | RequestFunnelWithOneFlow;
};

export class RevalidationService {
  constructor(
    private readonly config: Config,
    private readonly logger: Logger,
  ) {}

  isTokenValid(token?: string | string[]) {
    return !!token && token === process.env.NEXT_PUBLIC_FUNNELS_REVALIDATION_SECRET;
  }

  verifySignature({ secret, payload, headers }: VerifySignaturesParams) {
    const wh = new Webhook(secret);

    return wh.verify(payload, headers);
  }

  async revalidate({ res, locale, funnelName, screenNames }: RevalidateFunnelParams) {
    const paths = [
      this.buildPath(locale, funnelName),
      ...screenNames.map((screenName) => this.buildPath(locale, funnelName, screenName)),
    ];

    await this.revalidatePaths({ res, paths });
  }

  async revalidateFlow({ res, data }: RevalidationStrategyParams) {
    const flow = data.payload.funnel?.flow;
    const funnel = data.payload.funnel;

    if (!flow) {
      this.logger.error(RevalidationErrorReason.EMPTY_FLOW);

      throw new RevalidationError(RevalidationErrorReason.EMPTY_FLOW);
    }

    const funnelName = removeLeadingSlash(funnel.link);
    const hasPreviousState = 'previous_initial_screen' in flow && 'previous_language' in flow;

    if (!hasPreviousState) {
      const screenNames = uniq(flow.screens.map((screen) => screen.id));

      await this.revalidate({ res, locale: flow.language, funnelName, screenNames });

      return;
    }

    const paths = [
      this.buildPath(flow.language, funnelName),
      this.buildPath(flow.language, funnelName, flow.initial_screen),
    ];

    if (flow.previous_language && flow.previous_initial_screen) {
      paths.push(this.buildPath(flow.previous_language, funnelName, flow.previous_initial_screen));
    }

    if (flow.previous_language && flow.previous_language !== flow.language) {
      paths.push(this.buildPath(flow.previous_language, funnelName));
    }

    await this.revalidatePaths({ res, paths: uniq(paths) });
  }

  async revalidateFunnel({ res, data }: RevalidationStrategyParams) {
    const flows = data.payload.funnel?.flows;
    const funnel = data.payload.funnel;

    if (!flows?.length) {
      this.logger.error(RevalidationErrorReason.EMPTY_FUNNEL);

      throw new RevalidationError(RevalidationErrorReason.EMPTY_FUNNEL);
    }

    const funnelName = removeLeadingSlash(funnel.link);
    const languages = uniq(flows.map(({ language }) => language));

    const paths = languages.flatMap((language) => {
      const languageFlows = flows.filter((flow) => flow.language === language);
      const screenNames = uniq(
        languageFlows.flatMap(({ screens }) => screens?.map(({ id }) => id) ?? []),
      );
      const languagePaths = [
        this.buildPath(language, funnelName),
        ...screenNames.map((screenName) => this.buildPath(language, funnelName, screenName)),
      ];

      return languagePaths;
    });

    await this.revalidatePaths({ res, paths });
  }

  private async revalidatePaths({ res, paths }: RevalidatePathsParams) {
    const pathsPerRequest = this.config.revalidation.pathsPerRequest;
    let nextIndex = 0;

    const workers = Array.from({ length: pathsPerRequest }, async () => {
      while (nextIndex < paths.length) {
        const path = paths[nextIndex];
        nextIndex += 1;

        try {
          await res.revalidate(path);
        } catch (error) {
          this.logger.error(`Failed to revalidate path ${path}: ${normalizeError(error)}`);
        }
      }
    });

    await Promise.all(workers);
  }

  private buildPath(locale: Languages, funnelName: string, screenName?: string) {
    const funnelPath = `/${locale}/${funnelName}`;
    const path = screenName ? `${funnelPath}/${screenName}` : funnelPath;

    return path;
  }
}
